import { useEffect, useMemo, useRef, useState } from 'react';
import QRCodePlayStore from '../../Images/QR_Code_Play_Store.png';
import QRCodeAppStore from '../../Images/QR_Code_App_Store.png';

const ANDROID_MARKET = 'market://details?id=host.exp.houtive.prod';
const ANDROID_WEB = 'https://play.google.com/store/apps/details?id=host.exp.houtive.prod';
const IOS_ITMS = 'itms-apps://apps.apple.com/app/id6670727864';
const IOS_WEB = 'https://apps.apple.com/app/id6670727864';

const IOS_DELAY_MS = 400;     // öppna iOS-prompt efter 0.4s (UI redan synligt)
const ANDROID_DELAY_MS = 600; // öppna Play (https) efter 0.6s

// Brand accent
const ACCENT_PURPLE = '#9D4EDD';

function detectPlatform() {
  try {
    if (navigator.userAgentData?.mobile) {
      const p = (navigator.userAgentData.platform || '').toLowerCase();
      if (p.includes('android')) return 'android';
      if (p.includes('ios') || p.includes('iphone') || p.includes('ipad')) return 'ios';
    }
  } catch {}
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  return 'desktop';
}

function CopyLinkButton({ text, label, onCopied }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.top = '-1000px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      onCopied?.();
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Kopiera länken:', text);
      setCopied(true);
      onCopied?.();
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={copy}
      className="w-full inline-flex items-center justify-center px-4 py-3 rounded-full font-semibold shadow transition-colors"
      style={{ backgroundColor: ACCENT_PURPLE, color: '#fff' }}
      aria-live="polite"
    >
      {copied ? 'Kopierad ✓' : label}
    </button>
  );
}

const ConfirmedInvitation = () => {
  const platform = useMemo(detectPlatform, []);
  const timerRef = useRef(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (platform === 'android') {
      window.location.href = ANDROID_MARKET;
      timerRef.current = setTimeout(() => {
        window.location.href = ANDROID_WEB;
      }, ANDROID_DELAY_MS);
    } else if (platform === 'ios') {
      timerRef.current = setTimeout(() => {
        window.location.href = IOS_ITMS;
      }, IOS_DELAY_MS); // 300–500 ms ger lagom "pre-stabilitet"
    }
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [platform]);

  const isDesktop = platform === 'desktop';
  const isIOS = platform === 'ios';
  const isAndroid = platform === 'android';

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <main className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-[420px] text-center">
          {/* Rubrik */}
          <h2 className="text-2xl sm:text-3xl font-semibold text-center leading-tight text-white mb-2">Du är redo! 🎉</h2>

          {/* Plattformsspecifik instruktion */}
          {isIOS && (
            <div className="space-y-2">
              <p className="text-base md:text-lg font-semibold text-white">Houtive öppnas i App Store.</p>
              <p className="text-sm text-white/80">
                Tryck <span className="font-semibold">”Öppna”</span> när popup-rutan visas.
              </p>
              {/* <p className="text-sm text-white/70">
                Tryckte du <span className="italic">”Avbryt”</span>?{' '}
                <button onClick={reloadPage} className="underline decoration-dotted">
                  Ladda om sidan
                </button>{' '}
                för att försöka igen.
              </p> */}
            </div>
          )}

          {isAndroid && (
            <div className="space-y-2">
              <p className="text-base md:text-lg font-semibold text-white">Houtive öppnas i Google Play.</p>
              <p className="text-sm text-white/80">
                Tryck <span className="font-semibold">”Fortsätt”</span> i bannern ovan för att öppna{' '}
                <span className="font-semibold">Houtive</span> i Play Store.
              </p>
            </div>
          )}

          {isDesktop && (
            <p className="text-center text-sm sm:text-base text-[#D6D6D6] leading-relaxed mb-3">
              Skanna QR-kod för att ladda ner <span className="font-semibold">Houtive</span> på din telefon.
            </p>
          )}

          {/* Mobilhjälpblock – visas alltid efter liten delay */}
          {(isIOS || isAndroid) && (
            <div className="space-y-3 mt-3">
              <p className="text-sm text-white/70">
                Fungerar det inte? Kopiera App Store-länken och öppna den manuellt.
              </p>

              <CopyLinkButton
                text={isIOS ? IOS_WEB : ANDROID_WEB}
                label={isIOS ? 'Kopiera App Store-länk' : 'Kopiera Google Play-länk'}
                onCopied={() => setCopied(true)}
              />

              {/* Feedbacktext – flyttas inte layouten */}
              <div
                className={`transition-opacity duration-300 ${
                  copied ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              >
                <p className="text-sm font-semibold mt-1" style={{ color: ACCENT_PURPLE }}>
                  Länk kopierad ✓
                </p>
                <p className="text-sm text-white mt-1">
                  Öppna en ny flik i webbläsaren och klistra in länken för att öppna{' '}
                  <span className="font-semibold">Houtive</span> i {isIOS ? 'App Store' : 'Google Play'}.
                </p>
              </div>
            </div>
          )}

          {/* Desktop – QR-koder */}
          {isDesktop && (
            <div className="w-full mt-6 space-y-3">
              <div className="flex flex-col md:flex-row gap-6 justify-center">
                {/* iPhone */}
                <div className="flex-1 flex flex-col items-center">
                  <figure className="flex flex-col items-center">
                    <a href={IOS_WEB} target="_blank" rel="noopener noreferrer" className="inline-block max-w-[180px]">
                      <img src={QRCodeAppStore} alt="QR-kod – iPhone (App Store)" className="w-full h-auto mb-2" />
                    </a>
                    <figcaption className="text-xs text-white/80">iPhone (App Store)</figcaption>
                    <a
                      href={IOS_WEB}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 text-xs text-white/70 underline decoration-dotted"
                    >
                      Öppna i webbläsaren
                    </a>
                  </figure>
                </div>

                {/* Android */}
                <div className="flex-1 flex flex-col items-center">
                  <figure className="flex flex-col items-center">
                    <a href={ANDROID_WEB} target="_blank" rel="noopener noreferrer" className="inline-block max-w-[180px]">
                      <img src={QRCodePlayStore} alt="QR-kod – Android (Google Play)" className="w-full h-auto mb-2" />
                    </a>
                    <figcaption className="text-xs text-white/80">Android (Google Play)</figcaption>
                    <a
                      href={ANDROID_WEB}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 text-xs text-white/70 underline decoration-dotted"
                    >
                      Öppna i webbläsaren
                    </a>
                  </figure>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <footer className="h-4 md:h-6" />
    </div>
  );
};

export default ConfirmedInvitation;
